import PropTypes from 'prop-types';
import React from 'react';
import Immutable from 'immutable';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { withRouter } from '@bibliocommons/utils-routing';
import { Layout, MainContent, Sidebar } from '@bibliocommons/base-drawer';
import { libraryShape, accountShape, routerShape } from '@bibliocommons/bc-prop-types';
import { setFocusWithinElement } from '@bibliocommons/utils-accessibility';

import DrawerActions from 'app/actions/DrawerActions';
import MessagesActions from 'app/actions/MessagesActions';

import { currentLibrarySelector } from 'app/selectors/LibrarySelector';
import { getSummary, getBorrowingHistorySummary } from 'app/selectors/BorrowingSelector';
import { currentAccountSelector, getAccountsByLibraryId } from 'app/selectors/AuthSelector';
import { getILSQuotas } from 'app/selectors/HoldsSelector';
import MyBorrowingSidebar from 'app/components/borrowing/sidebar/MyBorrowingSidebar/MyBorrowingSidebar';
import BorrowingSidebarToggleButton from 'app/components/borrowing/sidebar/BorrowingSidebarToggleButton/BorrowingSidebarToggleButton';
import AccountSwitcherContainer from 'app/components/borrowing/sidebar/AccountSwitcher/AccountSwitcherContainer';

import './BorrowingLayout.scss';

const getActiveSection = path => {
  const ctrl = path.split('/')[2];
  if (['checkedout', 'holds', 'fines', 'recentlyreturned', 'interlibraryloans', 'accountmessages'].includes(ctrl)) {
    return ctrl;
  }

  return 'other';
};

export class BorrowingLayout extends React.PureComponent {
  componentDidMount() {
    const accountId = this.props.account.get('id');
    if (this.props.library.get('accountMessages')) {
      this.props.messagesActions.fetchMessages({ accountId });
    }
  }

  componentDidUpdate(prevProps) {
    const { router } = this.props;
    const pathnameChanged = router.location.pathname !== prevProps.router.location.pathname;

    if (pathnameChanged && this._contentRef) {
      setFocusWithinElement(this._contentRef);
    }
  }

  render() {
    const {
      children,
      account,
      drawerActions,
      checkoutSummary,
      holdsSummary,
      totalFines,
      totalBorrowingHistory,
      messages,
      drawer,
      library,
      ILSQuotas,
      accountsByLibraryId,
      illSummary,
      router,
      accruingFines
    } = this.props;

    const active = getActiveSection(router.location.pathname);

    return (
      <div className="cp-borrowing-layout container">
        <Layout open={drawer.get('open')}>
          <Sidebar sidebarClass="borrowing-widget-sidebar" drawerActions={drawerActions}>
            <MyBorrowingSidebar
              account={account}
              drawer={drawer}
              activeSection={active}
              libraryName={library.get('fullName', '')}
              linkableLibraries={library.get('linkableLibraries', Immutable.List())}
              holdsSummary={holdsSummary}
              checkoutSummary={checkoutSummary}
              accountsByLibraryId={accountsByLibraryId}
              totalFines={totalFines}
              totalBorrowingHistory={totalBorrowingHistory}
              drawerActions={drawerActions}
              ILSQuotas={ILSQuotas}
              messages={messages}
              isRecentlyReturnedEnabled={library.get('recentlyReturned')}
              isIllRequestsEnabled={library.get('illRequests')}
              illSummary={illSummary}
              isAccountMessagesEnabled={library.get('accountMessages')}
              isRemainingHoldsEnabled={library.get('remainingHolds')}
              showCardExpiryDate={library.get('cardExpiryDate')}
              accruingFines={accruingFines}
            />
          </Sidebar>
          <MainContent mainClass="borrowing-content">
            <BorrowingSidebarToggleButton drawerActions={drawerActions} />
            <span className="top-accout-swticher">
              <AccountSwitcherContainer />
            </span>
            <div ref={el => (this._contentRef = el)}>{children}</div>
          </MainContent>
        </Layout>
      </div>
    );
  }
}

const mapStateToProps = state => {
  const finesSummary = getSummary(state, 'fines');
  return {
    drawer: state.get('drawer'),
    checkoutSummary: getSummary(state, 'checkedout'),
    holdsSummary: getSummary(state, 'holds'),
    totalBorrowingHistory: getBorrowingHistorySummary(state),
    illSummary: getSummary(state, 'interlibraryLoans'),
    messages: state.getIn(['entities', 'messages']),
    account: currentAccountSelector(state),
    library: currentLibrarySelector(state),
    accountsByLibraryId: getAccountsByLibraryId(state),
    ILSQuotas: getILSQuotas(state),
    totalFines: finesSummary.get('totalFines', 0),
    accruingFines: finesSummary.get('totalAccrued', 0)
  };
};

const mapDispatchToProps = dispatch => ({
  drawerActions: bindActionCreators(DrawerActions, dispatch),
  messagesActions: bindActionCreators(MessagesActions, dispatch)
});

BorrowingLayout.propTypes = {
  account: accountShape,
  drawer: ImmutablePropTypes.mapContains({
    open: PropTypes.bool
  }).isRequired,
  checkoutSummary: ImmutablePropTypes.mapContains({
    nextDue: ImmutablePropTypes.mapContains({
      date: PropTypes.string,
      status: PropTypes.string
    }),
    status: ImmutablePropTypes.map
  }),
  holdsSummary: ImmutablePropTypes.mapContains({
    status: ImmutablePropTypes.map
  }),
  totalFines: PropTypes.number.isRequired,
  totalBorrowingHistory: PropTypes.number,
  messages: ImmutablePropTypes.map.isRequired,
  ILSQuotas: ImmutablePropTypes.map,
  drawerActions: PropTypes.objectOf(PropTypes.func).isRequired,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
  library: libraryShape,
  router: routerShape,
  accountsByLibraryId: ImmutablePropTypes.mapOf(accountShape),
  messagesActions: PropTypes.objectOf(PropTypes.func),
  accruingFines: PropTypes.number,
  illSummary: ImmutablePropTypes.map
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(BorrowingLayout));
